import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, X, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { subscribeDevLogs, clearDevLogs, type DevLogEntry } from '@/lib/devErrorLogger';

const DevErrorPanel = () => {
  const [entries, setEntries] = useState<DevLogEntry[]>([]);
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [searchParams] = useSearchParams();
  const debugOverride = searchParams.get('debug') === '1';
  const isDev = import.meta.env.MODE === 'development' && !import.meta.env.PROD;
  const showPanel = isDev || debugOverride;

  useEffect(() => {
    if (!showPanel) return;
    return subscribeDevLogs(setEntries);
  }, [showPanel]);

  if (!showPanel || hidden) return null;

  const errors = entries.filter((e) => e.type !== 'console.warn').length;
  const warns = entries.filter((e) => e.type === 'console.warn').length;

  return (
    <div
      className="fixed bottom-4 right-4 z-[9999] w-[min(420px,calc(100vw-2rem))] rounded-lg border border-border bg-background shadow-2xl text-sm font-mono"
      role="complementary"
      aria-label="Dev error logger"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 rounded-t-lg bg-destructive/10 px-3 py-2 text-destructive hover:bg-destructive/20"
      >
        <span className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" aria-hidden="true" />
          <span className="font-semibold">Dev logger</span>
          <span className="text-xs">{errors} err · {warns} warn</span>
        </span>
        <span className="flex items-center gap-1">
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
          <span
            role="button"
            tabIndex={0}
            aria-label="Close dev logger"
            onClick={(e) => {
              e.stopPropagation();
              setHidden(true);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                setHidden(true);
              }
            }}
            className="ml-1 rounded p-0.5 hover:bg-destructive/30"
          >
            <X className="h-4 w-4" />
          </span>
        </span>
      </button>

      {open && (
        <div className="max-h-[50vh] overflow-auto p-2">
          <div className="mb-2 flex justify-end">
            <button
              type="button"
              onClick={clearDevLogs}
              className="flex items-center gap-1 rounded border border-border px-2 py-1 text-xs hover:bg-muted"
            >
              <Trash2 className="h-3 w-3" /> Wyczyść
            </button>
          </div>
          {entries.length === 0 ? (
            <p className="p-2 text-muted-foreground">Brak zdarzeń.</p>
          ) : (
            <ul className="space-y-1">
              {entries.map((e) => (
                <li key={e.id} className="rounded border border-border p-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs uppercase text-muted-foreground">
                      {e.type}{e.status ? ` · ${e.status}` : ''}
                    </span>
                    <span className="text-xs text-muted-foreground">×{e.count}</span>
                  </div>
                  <div className="mt-1 break-all text-xs text-foreground">{e.message}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default DevErrorPanel;
